import React, { useEffect, useState } from 'react';
import Modal from '../../modals/Modal';
import Spinner from '../../Spinner';
import UserViewPlanModal from './UserViewPlanModal';
import { fetchTeamSubscriptionUsers } from '../../../services/admin/subscriptionTrackerApi';
import { TeamUserSubscriptionItem, TeamUserSubscriptionListResponse } from '../../../types/admin/subscriptionTracker';
import { formatLastLogin } from '../../../utils/admin/formatSubscriptionDate';
import UserSubPagination from './UserSubPagination';

interface ViewUsersModalProps {
  isOpen: boolean;
  teamId: number | null;
  teamName: string;
  onClose: () => void;
}

function formatSeat(user: TeamUserSubscriptionItem): string {
  if (user.seat_no == null) return '—';
  return String(user.seat_no);
}

function ActiveBadge({ isActive }: { isActive: boolean | null }) {
  if (isActive === true) {
    return (
      <span className="px-1.5 py-0.5 text-xs rounded bg-green-100 text-green-800">Active</span>
    );
  }
  if (isActive === false) {
    return (
      <span className="px-1.5 py-0.5 text-xs rounded bg-gray-200 text-gray-700">Inactive</span>
    );
  }
  return null;
}

function RoleBadges({ user }: { user: TeamUserSubscriptionItem }) {
  if (!user.is_admin && !user.team_admin) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {user.is_admin && (
        <span className="px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-800">Platform Admin</span>
      )}
      {user.team_admin && (
        <span className="px-1.5 py-0.5 text-xs rounded bg-indigo-100 text-indigo-800">Team Admin</span>
      )}
    </span>
  );
}

const ViewUsersModal: React.FC<ViewUsersModalProps> = ({
  isOpen,
  teamId,
  teamName,
  onClose,
}) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [limit] = useState(50);

  const [data, setData] = useState<TeamUserSubscriptionListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [planUser, setPlanUser] = useState<{
    userId: number;
    username: string;
    email: string;
  } | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setDebouncedSearch('');
      setOffset(0);
      setData(null);
      setError(null);
      setPlanUser(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setOffset(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!isOpen || teamId == null) return;

    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError(null);

      const result = await fetchTeamSubscriptionUsers(
        teamId,
        {
          search: debouncedSearch || undefined,
          limit,
          offset,
        },
        controller.signal
      );

      if (result.aborted || controller.signal.aborted) return;

      if (result.status === 404) {
        setError('Team not found');
        setData(null);
      } else if (result.data) {
        setData(result.data);
      } else {
        setError(result.error ?? 'Failed to load team users');
        setData(null);
      }

      setLoading(false);
    };

    load();

    return () => {
      controller.abort();
    };
  }, [isOpen, teamId, debouncedSearch, limit, offset]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        closeOnBackdrop={!planUser}
        closeOnEsc={!planUser}
        panelClassName="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col relative mx-4"
        backdropClassName="fixed inset-0 flex items-center justify-center bg-black/50 z-50 px-4"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mr-8">{teamName} — Users</h2>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && !data ? (
            <div className="flex justify-center items-center h-40">
              <Spinner size="md" />
              <span className="ml-2 text-gray-600">Loading users...</span>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>
              )}

              {data && (
                <>
                  <div className="mb-3 max-w-sm">
                    <input
                      type="text"
                      className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Search by username or email"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>

                  {loading && (
                    <div className="mb-2 flex items-center gap-2 text-sm text-gray-600">
                      <Spinner size="sm" />
                      <span>Updating...</span>
                    </div>
                  )}

                  {data.users.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No seated users found.</p>
                  ) : (
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Seat</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last login</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Logins</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {data.users.map((user) => (
                            <tr key={user.user_id}>
                              <td className="px-3 py-2 font-medium text-gray-900">{user.username}</td>
                              <td className="px-3 py-2 text-gray-600">{user.email?.trim() ? user.email : '—'}</td>
                              <td className="px-3 py-2 text-gray-600">{formatSeat(user)}</td>
                              <td className="px-3 py-2">
                                <ActiveBadge isActive={user.is_active} />
                              </td>
                              <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                                {formatLastLogin(user.last_login)}
                              </td>
                              <td className="px-3 py-2 text-gray-700 text-right">{user.login_count ?? 0}</td>
                              <td className="px-3 py-2">
                                <RoleBadges user={user} />
                              </td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setPlanUser({
                                      userId: user.user_id,
                                      username: user.username,
                                      email: user.email,
                                    })
                                  }
                                  className="px-3 py-1 text-sm text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50"
                                >
                                  View Plan
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {data.pagination && (
                    <UserSubPagination
                      pagination={data.pagination}
                      onOffsetChange={setOffset}
                      onLimitChange={() => {}}
                      showPageSize={false}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </Modal>

      <UserViewPlanModal
        isOpen={planUser != null}
        userId={planUser?.userId ?? null}
        username={planUser?.username ?? ''}
        email={planUser?.email ?? ''}
        teamId={teamId}
        allowAll
        onClose={() => setPlanUser(null)}
      />
    </>
  );
};

export default ViewUsersModal;
